import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { ProtectedRoute, homePathFor } from "@/auth/ProtectedRoute";
import { ToastProvider } from "@/components/ui";
import { LoginPage } from "@/pages/LoginPage";
import {
  ExamRunnerPage,
  SetupPage,
  MyProgressPage,
  StudentHomePage,
  StudentResultPage,
  SubmittedPage,
} from "@/pages/student";
import { PhonePage } from "@/pages/phone";
import { AdminHome } from "@/pages/admin";
import { NotFoundPage } from "@/pages/NotFoundPage";

/**
 * Route map. Each group is owned by a different worker — add child routes
 * inside your own group only.
 *
 *   /login       scaffold (auth)
 *   /student/*   student exam UI worker   (requires role=student)
 *   /phone/*     phone side-camera worker (public: joined by pair code)
 *   /admin/*     admin panel worker       (requires role=admin)
 */
export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />

            {/* Phone camera: reached on a second device via pair code. */}
            <Route path="/phone" element={<PhonePage />} />
            <Route path="/phone/:code" element={<PhonePage />} />

            <Route element={<ProtectedRoute role="student" />}>
              <Route path="/student" element={<StudentHomePage />} />
              <Route path="/student/progress" element={<MyProgressPage />} />
              {/* Setup runs BEFORE an attempt exists, so it is keyed by exam
                  id; the other two are keyed by the attempt it creates. */}
              <Route
                path="/student/exam/:examId/setup"
                element={<SetupPage />}
              />
              <Route
                path="/student/exam/:attemptId/run"
                element={<ExamRunnerPage />}
              />
              <Route
                path="/student/exam/:attemptId/submitted"
                element={<SubmittedPage />}
              />
              <Route
                path="/student/exam/:attemptId/result"
                element={<StudentResultPage />}
              />
            </Route>

            <Route element={<ProtectedRoute role="admin" />}>
              <Route path="/admin" element={<AdminHome />} />
              <Route path="/admin/*" element={<AdminHome />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

/** Sends each role to its own home; anonymous visitors to /login. */
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? homePathFor(user.role) : "/login"} replace />;
}
